import { useEffect, useState } from 'react'
import { AlertIcon, CheckIcon } from '../components/icons'
import { JobIcon } from '../components/JobIcon'
import { TyreAxleQuantities } from '../components/TyreAxleQuantities'
import { TyreFitmentPicker } from '../components/TyreFitmentPicker'
import { TyreSizeEntry } from '../components/TyreSizeEntry'
import { sanitizeRegistration } from '../lib/format'
import {
  clampQuantity,
  describePosition,
  MAX_TYRE_QUANTITY,
  type TyreSelection,
  type TyreSizeChoice,
} from '../lib/tyres'
import type { ResolveWidgetVehicleResponse, TyreFitment } from '../types/PublicBookingChannelTypes'

export interface TyreStartStepProps {
  businessName: string

  registration: string
  onRegistrationChange: (value: string) => void
  onLookup: () => void
  lookupStatus: 'idle' | 'loading' | 'success' | 'error'
  vehicle: ResolveWidgetVehicleResponse | null
  lookupError: string | null

  /**
   * Sizes from the registration lookup. Only a car the garage already holds has
   * any; the lookup never pays for tyre data, so most cars come back without.
   */
  vehicleSizes: TyreSizeChoice[]
  /** The car's factory fitments, standard first (ACA-98). Offered ahead of held sizes. */
  fitments?: TyreFitment[]
  selection: TyreSelection | null
  onSelectionChange: (selection: TyreSelection | null) => void
}

type Mode = 'registration' | 'size'

/**
 * The opening screen of a tyres-only channel: registration or size, then how many.
 *
 * Two entry points on purpose. Most people would rather type a plate than crouch
 * by a wheel reading a sidewall, but anyone who already knows their size — or
 * whose plate we cannot resolve, or who is buying for a car that is not theirs —
 * must not be stuck behind a lookup. Either route lands on the same choice.
 *
 * No registration is required to see prices. The booking needs one eventually,
 * so it is collected with the customer's other details, once they have seen what
 * the tyres cost.
 */
export function TyreStartStep({
  businessName,
  registration,
  onRegistrationChange,
  onLookup,
  lookupStatus,
  vehicle,
  lookupError,
  vehicleSizes,
  fitments = [],
  selection,
  onSelectionChange,
}: TyreStartStepProps) {
  const hasFitments = fitments.length > 0
  const foundWithoutSizes =
    lookupStatus === 'success' && vehicle != null && vehicleSizes.length === 0 && !hasFitments
  const [mode, setMode] = useState<Mode>(
    selection?.size.source === 'manual' || foundWithoutSizes ? 'size' : 'registration'
  )

  // A car with no sizes we know of goes straight to typing the size rather
  // than a dead end. Once per answer: a customer who switches back to the
  // registration stays there.
  useEffect(() => {
    if (lookupStatus !== 'success' || !vehicle || vehicleSizes.length > 0 || hasFitments) return
    setMode('size')
  }, [hasFitments, lookupStatus, vehicle, vehicleSizes.length])

  // Two, not four. Pairs are the common purchase — one axle at a time — and
  // defaulting to a full set inflates the first price a customer sees by double.
  const quantity = selection ? clampQuantity(selection.quantity) : 2

  const switchTo = (next: Mode) => {
    if (next === mode) return
    setMode(next)
    // The two routes produce different sizes, so a half-made choice from the
    // other one must not linger.
    onSelectionChange(null)
  }

  return (
    <div className="ac-stack">
      <div className="ac-stack ac-stack--tight">
        <p className="ac-eyebrow">
          <JobIcon name="tyres" size={14} /> {businessName}
        </p>
        <h3 className="ac-heading ac-heading--display">Find your tyres</h3>
        <p className="ac-prose">Prices include fitting, balancing and VAT.</p>
      </div>

      <div className="ac-toggle" role="tablist" aria-label="How to find your tyres">
        <button
          type="button"
          role="tab"
          aria-selected={mode === 'registration'}
          className={`ac-toggle__option${mode === 'registration' ? ' ac-toggle__option--on' : ''}`}
          onClick={() => switchTo('registration')}
        >
          By registration
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={mode === 'size'}
          className={`ac-toggle__option${mode === 'size' ? ' ac-toggle__option--on' : ''}`}
          onClick={() => switchTo('size')}
        >
          I know my size
        </button>
      </div>

      {mode === 'registration' ? (
        <div className="ac-stack ac-stack--tight">
          <div className="ac-plate">
            <span className="ac-plate__country" aria-hidden="true">
              <span className="ac-plate__stars">★</span>
              GB
            </span>
            <input
              type="text"
              className="ac-plate__input"
              placeholder="AB12 CDE"
              aria-label="Vehicle registration"
              value={registration}
              onChange={(event) => onRegistrationChange(sanitizeRegistration(event.target.value))}
              onKeyDown={(event) => {
                if (event.key !== 'Enter') return
                event.preventDefault()
                onLookup()
              }}
              maxLength={8}
              autoComplete="off"
              autoCapitalize="characters"
              spellCheck={false}
              enterKeyHint="search"
              aria-invalid={lookupStatus === 'error'}
            />
          </div>

          {lookupStatus !== 'success' ? (
            <button
              type="button"
              className="ac-btn ac-btn--secondary"
              onClick={onLookup}
              disabled={lookupStatus === 'loading' || registration.trim().length === 0}
            >
              {lookupStatus === 'loading' ? (
                <>
                  <span className="ac-spinner" aria-hidden="true" />
                  Looking up
                </>
              ) : (
                'Find my tyre size'
              )}
            </button>
          ) : null}

          <div aria-live="polite">
            {vehicle ? (
              <div className="ac-vehicle">
                <CheckIcon size={20} />
                <span className="ac-vehicle__name">
                  {vehicle.vehicleMake} {vehicle.vehicleModel}
                </span>
              </div>
            ) : null}

            {lookupError ? (
              <div className="ac-notice ac-notice--error" role="alert">
                <AlertIcon size={18} />
                <span>{lookupError}</span>
              </div>
            ) : null}
          </div>

          {lookupStatus === 'success' && vehicleSizes.length === 0 && !hasFitments ? (
            <div className="ac-notice ac-notice--info">
              <span>
                We don&apos;t hold tyre sizes for this vehicle. Switch to “I know my size” and
                type it off the sidewall.
              </span>
            </div>
          ) : null}

          {hasFitments && vehicle ? (
            <TyreFitmentPicker
              fitments={fitments}
              selection={selection}
              onSelectionChange={onSelectionChange}
              onOtherSize={() => switchTo('size')}
            />
          ) : null}

          {!hasFitments && vehicleSizes.length > 0 ? (
            <ul className="ac-list">
              {vehicleSizes.map((size) => {
                const selected = selection?.size.size === size.size
                return (
                  <li key={size.size}>
                    <button
                      type="button"
                      className={`ac-option${selected ? ' ac-option--selected' : ''}`}
                      aria-pressed={selected}
                      onClick={() => onSelectionChange({ size, quantity })}
                    >
                      <span className="ac-option__check" aria-hidden="true">
                        <CheckIcon size={15} />
                      </span>
                      <span className="ac-option__main">
                        <span className="ac-option__name">{size.size}</span>
                        <span className="ac-option__desc">{describePosition(size)}</span>
                      </span>
                    </button>
                  </li>
                )
              })}
            </ul>
          ) : null}
        </div>
      ) : (
        <div className="ac-stack ac-stack--tight">
          {foundWithoutSizes ? (
            <>
              <div className="ac-vehicle">
                <CheckIcon size={20} />
                <span className="ac-vehicle__name">
                  {vehicle.vehicleMake} {vehicle.vehicleModel}
                </span>
              </div>
              <p className="ac-prose">Enter the size from the side of your tyre.</p>
            </>
          ) : null}

          <TyreSizeEntry selection={selection} onSelectionChange={onSelectionChange} />
        </div>
      )}

      {selection?.rear ? (
        <TyreAxleQuantities selection={selection} onSelectionChange={onSelectionChange} />
      ) : null}

      {selection && !selection.rear ? (
        <div className="ac-stack ac-stack--tight">
          <p className="ac-label">How many tyres?</p>
          <div className="ac-quantity" role="group" aria-label="Number of tyres">
            {Array.from({ length: MAX_TYRE_QUANTITY }, (_, index) => index + 1).map((count) => {
              const selected = quantity === count
              return (
                <button
                  key={count}
                  type="button"
                  className={`ac-quantity__option${selected ? ' ac-quantity__option--selected' : ''}`}
                  aria-pressed={selected}
                  onClick={() => onSelectionChange({ ...selection, quantity: count })}
                >
                  {count}
                </button>
              )
            })}
          </div>
        </div>
      ) : null}
    </div>
  )
}
